import {LucideIcon} from "lucide-react"
import {cn} from "@/lib/utils"

export type CtaButtonIconProps = {
    icon: LucideIcon
    className?: string
    resolvedActiveClass?: string
    baseClass?: string
    ariaHidden?: boolean
}

/**
 * The part of an active treatment that belongs on the GLYPH: colour only (`fill-*`, `text-*`, `stroke-*`,
 * with or without `!` and any variant prefix).
 *
 * <p>`NOTIFICATIONS-58`: an active class is a CONTAINER treatment. `INBOX_TOGGLE_ACTIVE_CLASS` carries a
 * border, a fill and an inset ring, and applying the whole string to the SVG so its `!stroke-*` reached
 * the paths drew the pressed bell inside its own 1px square. The container keeps the full class (the root
 * gets it through `getCtaClasses`); the icon gets only what colours it. Parsed at bracket depth 0, so an
 * arbitrary variant such as `[@media(pointer:coarse)]:` or a `var(--x)` value is not split on its colon.
 */
export function pickIconActiveClass(activeClass: string): string {
    return activeClass
        .split(/\s+/)
        .filter((token) => {
            if (!token) {
                return false
            }
            let depth = 0
            let utilityStart = 0
            for (let index = 0; index < token.length; index += 1) {
                const character = token[index]
                if (character === "[" || character === "(") depth += 1
                else if (character === "]" || character === ")") depth = Math.max(0, depth - 1)
                else if (character === ":" && depth === 0) utilityStart = index + 1
            }
            const utility = token.slice(utilityStart).replace(/^!/, "")
            return /^(fill|text|stroke)-/.test(utility)
        })
        .join(" ")
}

/**
 * Renders a LucideIcon with unified base classes and optional resolvedActiveClass applied.
 * - `baseClass` lets callers adjust size/min-size (default matches existing small icons).
 * - the COLOUR part of `resolvedActiveClass` is applied first so active utilities (like `!stroke-*`) propagate to
 *   SVG children; its border, background and ring stay on the control (`pickIconActiveClass`).
 */
export function CtaButtonIcon({
    icon: Icon,
    className = "",
    resolvedActiveClass = "",
    baseClass = "fill-none stroke-2 h-6 w-6",
    ariaHidden = true,
}: CtaButtonIconProps) {
    return (
        <Icon
            className={cn(baseClass, resolvedActiveClass ? pickIconActiveClass(resolvedActiveClass) || "stroke-current" : "stroke-current", className, "cta-button-icon block align-middle")}
            aria-hidden={ariaHidden}
        />
    )
}
